import { t } from 'i18next';
import { Points } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { ProgressController } from '../../../../../../../react/utils/progress-controller';
import { PromiseAbortController } from '../../../../../../../react/utils/promise-abort-controller';
import { isDefined } from '../../../../../../../utils/variables';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import { getPointHeight, InterpolationSnapMode } from '../../utils/point';
import { selectPoints } from '../../utils/select';

function activateSetPointHeightsFeature(tool: DesignTool): ContextMenuItem {
  const intersectedPointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);
  const disabled = !isDefined(intersectedPointId) && tool.currentSelection.points.length === 0;

  return {
    title: t('contextMenu.functions.items.setHeight', { ns: 'skyviewDesign' }),
    action: () => {
      const intersectedPointId = findPointIdFromIntersection(
        tool.asset.designNode,
        tool.intersection,
      );

      if (isDefined(intersectedPointId) && tool.currentSelection.points.length === 0) {
        selectPoints(tool, [intersectedPointId]);
        tool.setPointHeightsValueNeedsUpdate = true;
      } else if (isDefined(tool.currentPoint)) {
        tool.setPointHeightsValueNeedsUpdate = true;
        publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
      }

      tool.activeFeature = DesignFeature.SetPointHeights;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        snapMode: false,
        placeMode: false,
        features: true,
      });
      tool.contextMenuCallback?.();
    },
    disabled,
    hotkey: 'H',
  };
}

async function getSetPointHeightResult(
  tool: DesignTool,
  pointIds: PointId[],
  snapMode: InterpolationSnapMode,
  value: number,
  abortController: PromiseAbortController,
  progressController: ProgressController,
  chunks: Points[],
): Promise<{ id: PointId; height: number }[]> {
  progressController.callback(
    0,
    `${progressController.step} ${
      t('contextMenu.updatingPoints', { ns: 'skyviewDesign' }) as string
    }`,
  );

  const result: { id: PointId; height: number }[] = [];
  for (let i = 0; i < pointIds.length; i++) {
    const point = tool.asset.designNode.getVertexByPointId(pointIds[i], 'world');
    const height = await getPointHeight(tool, point, snapMode, chunks, value);

    if (isDefined(height)) {
      result.push({ id: pointIds[i], height: height });
    }

    progressController.callback(i / pointIds.length);

    if (abortController.signal.aborted) {
      return [];
    }
  }

  progressController.incrementStep();

  return result;
}

function applySetPointHeight(tool: DesignTool, points: { id: PointId; height: number }[]) {
  let changes: RevertChange[] = [];

  addAndCommitAction(tool, {
    commit: () => {
      changes = [];

      for (const point of points) {
        const newPosition = tool.asset.designNode
          .getVertexByPointId(point.id, 'local')
          .setZ(point.height);

        changes.unshift(
          tool.asset.designNode.upsertPoint({
            id: point.id,
            vertex: newPosition,
          }),
        );
      }

      tool.asset.designNode.refresh();
      tool.refreshCurrentSelection();
      tool.asset.refreshMetadata();
    },
    rollback: () => {
      tool.asset.designNode.revert(changes);
      tool.asset.designNode.refresh();
      tool.asset.refreshMetadata();
    },
  });
}

export const pointHeightFunctions = {
  activateSetPointHeightsFeature,
  getSetPointHeightResult,
  applySetPointHeight,
};
